import { useEffect, useState, useCallback } from "react";
import { 
  getAllPOs, 
  uploadPOs, 
  deletePO, 
  previewInvoice,
  generateInvoice,
  getInvoicePDF,
  updatePOStatus,
  bulkGenerateInvoices,
  bulkClosePOs,
  getStores,
  getInventory,
  createManualPO
} from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Upload, 
  FileText, 
  Trash2, 
  Printer, 
  Download,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  X,
  RefreshCw,
  Calendar,
  Zap,
  CheckCheck,
  Plus,
  Package,
  Store,
  Search
} from "lucide-react";
import { toast } from "sonner";

export default function POProcessing() {
  const [pos, setPOs] = useState([]);
  const [filteredPOs, setFilteredPOs] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkClosing, setBulkClosing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [dateFilter, setDateFilter] = useState("all");
  const [uploadDates, setUploadDates] = useState([]);
  const [showManualPO, setShowManualPO] = useState(false);

  useEffect(() => {
    loadPOs();
  }, []);

  // Extract unique upload dates and filter POs
  useEffect(() => {
    const dates = [...new Set(pos.map(po => {
      const date = new Date(po.created_at);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }))].sort((a, b) => new Date(b) - new Date(a));
    setUploadDates(dates);
    
    if (dateFilter === "all") {
      setFilteredPOs(pos);
    } else {
      setFilteredPOs(pos.filter(po => {
        const poDate = new Date(po.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        return poDate === dateFilter;
      }));
    }
  }, [pos, dateFilter]);

  // Count pending and invoiced POs for current filter
  const pendingCount = filteredPOs.filter(po => po.status === 'pending').length;
  const invoicedCount = filteredPOs.filter(po => po.status === 'invoiced').length;

  const loadPOs = async () => {
    try {
      const response = await getAllPOs();
      setPOs(response.data);
    } catch (error) {
      toast.error("Failed to load POs");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    try {
      const response = await uploadPOs(Array.from(files));
      const results = response.data.results;
      
      const successful = results.filter(r => r.status === 'success');
      const failed = results.filter(r => r.status === 'error');
      
      if (successful.length > 0) {
        toast.success(`Uploaded ${successful.length} PO(s) successfully`);
      }
      if (failed.length > 0) {
        toast.error(`Failed to upload ${failed.length} file(s)`);
      }
      
      await loadPOs();
    } catch (error) {
      toast.error("Failed to upload files");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleSelectPO = async (po) => {
    setSelectedPO(po);
    setPreview(null);
    
    if (po.status === 'pending') {
      try {
        const response = await previewInvoice(po.id);
        setPreview(response.data);
      } catch (error) {
        toast.error("Failed to load preview");
      }
    }
  };

  const handleGenerateInvoice = async () => {
    if (!selectedPO) return;
    
    setGenerating(true);
    try {
      const response = await generateInvoice(selectedPO.id);
      toast.success(`Invoice ${response.data.invoice_number} generated!`);
      await loadPOs();
      setSelectedPO({ ...selectedPO, status: 'invoiced', invoice_id: response.data.id });
      setPreview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate invoice");
    } finally {
      setGenerating(false);
    }
  };

  const handlePrintInvoice = async () => {
    if (!selectedPO?.invoice_id) return;
    
    try {
      const response = await getInvoicePDF(selectedPO.invoice_id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
      await updatePOStatus(selectedPO.id, 'printed');
    } catch (error) {
      toast.error("Failed to print invoice");
    }
  };

  const handleDownloadInvoice = async () => {
    if (!selectedPO?.invoice_id) return;
    
    try {
      const response = await getInvoicePDF(selectedPO.invoice_id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedPO.po_number}_invoice.pdf`;
      a.click();
    } catch (error) {
      toast.error("Failed to download invoice");
    }
  };

  const handleClosePO = async () => {
    if (!selectedPO) return;
    
    try {
      await updatePOStatus(selectedPO.id, 'closed');
      toast.success("PO closed successfully");
      await loadPOs();
      setSelectedPO(null);
      setPreview(null);
    } catch (error) {
      toast.error("Failed to close PO");
    }
  };

  const handleDeletePO = async (po) => {
    if (!window.confirm(`Delete PO ${po.po_number}?`)) return;
    
    try {
      await deletePO(po.id);
      toast.success("PO deleted");
      await loadPOs();
      if (selectedPO?.id === po.id) {
        setSelectedPO(null);
        setPreview(null);
      }
    } catch (error) {
      toast.error("Failed to delete PO");
    }
  };

  // Bulk action handlers
  const handleBulkGenerate = async () => {
    if (pendingCount === 0) {
      toast.error("No pending POs to generate");
      return;
    }
    
    if (!window.confirm(`Generate invoices for all ${pendingCount} pending POs${dateFilter !== 'all' ? ` from ${dateFilter}` : ''}?`)) return;
    
    setBulkGenerating(true);
    try {
      const payload = dateFilter !== 'all' 
        ? { date_filter: dateFilter }
        : { po_ids: filteredPOs.filter(po => po.status === 'pending').map(po => po.id) };
      
      const response = await bulkGenerateInvoices(payload);
      const { successful, total_processed, invoice_batch_id } = response.data;
      
      toast.success(`Generated ${successful}/${total_processed} invoices`, {
        description: invoice_batch_id ? `Batch ID: ${invoice_batch_id.slice(0, 8)}` : undefined
      });
      
      await loadPOs();
      setSelectedPO(null);
      setPreview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate invoices");
    } finally {
      setBulkGenerating(false);
    }
  };

  const handleBulkClose = async () => {
    if (invoicedCount === 0) {
      toast.error("No invoiced POs to close");
      return;
    }
    
    if (!window.confirm(`Close all ${invoicedCount} invoiced POs${dateFilter !== 'all' ? ` from ${dateFilter}` : ''}?`)) return;
    
    setBulkClosing(true);
    try {
      const payload = dateFilter !== 'all' 
        ? { date_filter: dateFilter, status_filter: 'invoiced' }
        : { po_ids: filteredPOs.filter(po => po.status === 'invoiced').map(po => po.id) };
      
      const response = await bulkClosePOs(payload);
      toast.success(`Closed ${response.data.modified_count} POs`);
      await loadPOs();
      setSelectedPO(null);
      setPreview(null);
    } catch (error) {
      toast.error("Failed to close POs");
    } finally {
      setBulkClosing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-900"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'invoiced':
        return <Badge className="bg-emerald-100 text-emerald-900"><CheckCircle className="w-3 h-3 mr-1" />Invoiced</Badge>;
      case 'closed':
        return <Badge variant="outline"><XCircle className="w-3 h-3 mr-1" />Closed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="po-processing">
      {/* Upload section */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700">Upload PO PDFs</h3>
            <Button size="sm" variant="outline" onClick={() => setShowManualPO(true)} data-testid="manual-po-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Create Manual PO
            </Button>
          </div>
          <div
            className={`upload-zone rounded-lg p-8 text-center cursor-pointer ${dragOver ? 'drag-over' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById('po-upload').click()}
            data-testid="upload-zone"
          >
            <Input
              id="po-upload"
              type="file"
              multiple
              accept=".pdf"
              className="hidden"
              onChange={(e) => handleFileUpload(e.target.files)}
            />
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <div className="spinner" />
                <p className="text-slate-600">Uploading...</p>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-slate-700">Drop PO PDFs here or click to upload</p>
                <p className="text-sm text-slate-500 mt-2">Supports multiple files. PDF format only.</p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Date Tabs */}
      {uploadDates.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <Calendar className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <Tabs value={dateFilter} onValueChange={setDateFilter}>
            <TabsList className="h-9">
              <TabsTrigger value="all" className="text-xs px-3">
                All ({pos.length})
              </TabsTrigger>
              {uploadDates.slice(0, 7).map(date => (
                <TabsTrigger key={date} value={date} className="text-xs px-3">
                  {date} ({pos.filter(po => {
                    const poDate = new Date(po.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                    return poDate === date;
                  }).length})
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {(pendingCount > 0 || invoicedCount > 0) && (
        <Card className="bg-slate-900 text-white">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-lg">Bulk Actions</h3>
                <p className="text-slate-400 text-sm">
                  {dateFilter !== 'all' ? `For ${dateFilter}` : 'For all POs'} - {pendingCount} pending, {invoicedCount} invoiced
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Button 
                  onClick={handleBulkGenerate}
                  disabled={bulkGenerating || pendingCount === 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="bulk-generate-btn"
                >
                  {bulkGenerating ? (
                    <><div className="spinner w-4 h-4 mr-2" />Generating...</>
                  ) : (
                    <><Zap className="w-4 h-4 mr-2" />Generate All ({pendingCount})</>
                  )}
                </Button>
                <Button 
                  variant="secondary"
                  onClick={handleBulkClose}
                  disabled={bulkClosing || invoicedCount === 0}
                  data-testid="bulk-close-btn"
                >
                  {bulkClosing ? (
                    <><div className="spinner w-4 h-4 mr-2" />Closing...</>
                  ) : (
                    <><CheckCheck className="w-4 h-4 mr-2" />Close All ({invoicedCount})</>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main content - Split view */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" style={{ minHeight: 'calc(100vh - 22rem)' }}>
        {/* PO List */}
        <Card className="lg:col-span-4 overflow-hidden">
          <CardHeader className="py-3 px-4 border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">
                Purchase Orders ({filteredPOs.length})
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={loadPOs}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <ScrollArea className="h-[450px]">
            {filteredPOs.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No POs found</p>
                <p className="text-sm mt-1">Upload PDFs to get started</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredPOs.map((po) => (
                  <div
                    key={po.id}
                    className={`po-item p-4 cursor-pointer ${selectedPO?.id === po.id ? 'active' : ''}`}
                    onClick={() => handleSelectPO(po)}
                    data-testid={`po-item-${po.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate font-mono text-sm">
                          {po.po_number}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {po.buyer_store || po.buyer_name}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {po.items?.length || 0} items • Rs. {(po.total_value || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {getStatusBadge(po.status)}
                        {po.status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                            onClick={(e) => { e.stopPropagation(); handleDeletePO(po); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </Card>

        {/* PO Detail / Invoice Preview */}
        <Card className="lg:col-span-8 overflow-hidden">
          {!selectedPO ? (
            <div className="h-full flex items-center justify-center text-slate-500 p-8">
              <div className="text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-lg">Select a PO to view details</p>
              </div>
            </div>
          ) : (
            <>
              <CardHeader className="py-3 px-4 border-b flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-mono">{selectedPO.po_number}</CardTitle>
                  <p className="text-sm text-slate-500">
                    {selectedPO.buyer_store} • {selectedPO.po_date}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {selectedPO.status === 'pending' && (
                    <Button 
                      onClick={handleGenerateInvoice}
                      disabled={generating || !preview?.items?.some(i => i.allocated)}
                      className="btn-hover"
                      data-testid="generate-invoice-btn"
                    >
                      {generating ? (
                        <><div className="spinner w-4 h-4 mr-2" />Generating...</>
                      ) : (
                        <><CheckCircle className="w-4 h-4 mr-2" />Generate Invoice</>
                      )}
                    </Button>
                  )}
                  {selectedPO.status === 'invoiced' && (
                    <>
                      <Button variant="outline" onClick={handleDownloadInvoice}>
                        <Download className="w-4 h-4 mr-2" />Download
                      </Button>
                      <Button onClick={handlePrintInvoice} className="btn-hover">
                        <Printer className="w-4 h-4 mr-2" />Print
                      </Button>
                      <Button variant="destructive" onClick={handleClosePO}>
                        <X className="w-4 h-4 mr-2" />Close PO
                      </Button>
                    </>
                  )}
                  {selectedPO.status === 'closed' && (
                    <Button variant="outline" onClick={handleDownloadInvoice}>
                      <Download className="w-4 h-4 mr-2" />Download Invoice
                    </Button>
                  )}
                </div>
              </CardHeader>
              
              <ScrollArea className="h-[400px]">
                <div className="p-6">
                  {preview && selectedPO.status === 'pending' && (
                    <div className="invoice-preview rounded-lg border border-slate-200 p-6 bg-white">
                      {(preview.has_stock_issues || preview.has_unmatched_items) && (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm">
                          <div className="flex items-center gap-2 text-amber-700">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="font-medium">Stock Allocation Issues</span>
                          </div>
                          <p className="text-sm text-amber-600 mt-1">
                            Some items may be skipped due to insufficient stock.
                          </p>
                        </div>
                      )}

                      <div className="flex justify-between items-start mb-6">
                        <div>
                          <p className="text-sm text-slate-500">COMMERCIAL</p>
                          <h2 className="text-2xl font-bold text-slate-900">INVOICE</h2>
                          <p className="text-slate-600 mt-1">{preview.invoice_date}</p>
                          <p className="text-sm text-slate-500">Due: {preview.payment_due_date}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-slate-900">VARDHMAN MARKETING</p>
                          <p className="text-sm text-slate-600">GSTIN: 27AKRPS3339K1ZE</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4 mb-6 pb-4 border-b">
                        <div>
                          <p className="text-xs font-semibold text-slate-500 uppercase">Bill To</p>
                          <p className="font-medium text-slate-900 mt-1">{preview.buyer_name}</p>
                          <p className="text-sm text-slate-600">GST: {preview.buyer_gst}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs font-semibold text-slate-500 uppercase">Store</p>
                          <p className="font-medium text-slate-900 mt-1">{preview.buyer_store || '-'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-semibold text-slate-500 uppercase">PO Ref</p>
                          <p className="font-mono text-slate-900 mt-1">{preview.po_number}</p>
                        </div>
                      </div>

                      <Table className="data-table">
                        <TableHeader>
                          <TableRow className="bg-slate-900 hover:bg-slate-900">
                            <TableHead className="text-white text-xs">SR</TableHead>
                            <TableHead className="text-white text-xs">PRODUCT</TableHead>
                            <TableHead className="text-white text-xs text-center">QTY</TableHead>
                            <TableHead className="text-white text-xs text-right">MRP</TableHead>
                            <TableHead className="text-white text-xs text-right">UNIT</TableHead>
                            <TableHead className="text-white text-xs text-right">TOTAL</TableHead>
                            <TableHead className="text-white text-xs text-center">STATUS</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {preview.items.map((item, idx) => (
                            <TableRow 
                              key={idx}
                              className={`${!item.matched ? 'bg-red-50' : ''} ${item.matched && !item.allocated ? 'bg-amber-50' : ''} ${item.allocated ? 'bg-emerald-50' : ''}`}
                            >
                              <TableCell className="font-mono text-xs">{item.allocated ? item.sr_no : '-'}</TableCell>
                              <TableCell>
                                <p className="font-medium text-sm">{item.product_name}</p>
                              </TableCell>
                              <TableCell className="text-center font-mono">
                                {item.allocated ? item.quantity : 0}
                                {item.original_qty_requested > item.quantity && (
                                  <span className="text-xs text-slate-400 ml-1">/{item.original_qty_requested}</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right font-mono">Rs. {item.mrp?.toLocaleString()}</TableCell>
                              <TableCell className="text-right font-mono">Rs. {item.unit_price?.toLocaleString()}</TableCell>
                              <TableCell className="text-right font-mono font-medium">Rs. {item.line_total?.toLocaleString()}</TableCell>
                              <TableCell className="text-center">
                                {!item.matched ? (
                                  <Badge className="bg-red-100 text-red-700 text-xs">Not Found</Badge>
                                ) : !item.allocated ? (
                                  <Badge className="bg-amber-100 text-amber-700 text-xs">Out of Stock</Badge>
                                ) : (
                                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">Allocated</Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      <div className="mt-6 flex justify-end">
                        <div className="w-72 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Total Pcs:</span>
                            <span className="font-mono">{preview.total_pcs}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Taxable Amount:</span>
                            <span className="font-mono">Rs. {preview.taxable_amount?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                          {preview.is_igst ? (
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">IGST ({preview.gst_rate || 18}%):</span>
                              <span className="font-mono">Rs. {preview.igst?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                          ) : (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">CGST ({(preview.gst_rate || 18) / 2}%):</span>
                                <span className="font-mono">Rs. {preview.cgst?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">SGST ({(preview.gst_rate || 18) / 2}%):</span>
                                <span className="font-mono">Rs. {preview.sgst?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                              </div>
                            </>
                          )}
                          <div className="flex justify-between font-bold text-lg pt-2 border-t">
                            <span>Grand Total:</span>
                            <span className="font-mono">Rs. {preview.grand_total?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {(selectedPO.status === 'invoiced' || selectedPO.status === 'closed') && (
                    <div className="text-center py-12">
                      <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                      <p className="text-xl font-semibold text-slate-900">Invoice Generated</p>
                      <p className="text-slate-600 mt-2">
                        {selectedPO.status === 'invoiced' ? 'Ready to print or download' : 'This PO has been closed'}
                      </p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </>
          )}
        </Card>
      </div>

      {/* Manual PO Modal */}
      {showManualPO && (
        <ManualPOModal onClose={() => setShowManualPO(false)} onCreated={() => { setShowManualPO(false); loadPOs(); }} />
      )}
    </div>
  );
}

function ManualPOModal({ onClose, onCreated }) {
  const [stores, setStores] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [storeSearch, setStoreSearch] = useState("");
  const [invSearch, setInvSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getStores(), getInventory()])
      .then(([storesRes, invRes]) => {
        setStores(storesRes.data.stores || []);
        setInventory((invRes.data || []).filter(i => i.quantity > 0));
      })
      .catch(() => toast.error("Failed to load data"))
      .finally(() => setLoading(false));
  }, []);

  const filteredStores = stores.filter(s =>
    s.store_name?.toLowerCase().includes(storeSearch.toLowerCase())
  );

  const filteredInventory = inventory.filter(i =>
    i.model_name?.toLowerCase().includes(invSearch.toLowerCase())
  );

  const toggleItem = (item) => {
    setSelectedItems(prev => {
      const exists = prev.find(i => i.sr_no === item.sr_no);
      if (exists) return prev.filter(i => i.sr_no !== item.sr_no);
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const updateQty = (srNo, qty) => {
    setSelectedItems(prev => prev.map(i => i.sr_no === srNo ? { ...i, quantity: Math.max(1, Math.min(qty, i.maxQty || i.quantity_available || 999)) } : i));
  };

  const handleCompleteBatch = () => {
    const batch = inventory.filter(i => i.quantity > 0).map(i => ({
      ...i,
      quantity: 1,
      maxQty: i.quantity,
      quantity_available: i.quantity
    }));
    setSelectedItems(batch);
    toast.success(`Added ${batch.length} in-stock SKUs (1 each)`);
  };

  const handleCreate = async () => {
    if (!selectedStore) { toast.error("Select a store"); return; }
    if (selectedItems.length === 0) { toast.error("Add at least one item"); return; }

    setCreating(true);
    try {
      const payload = {
        store_name: selectedStore,
        items: selectedItems.map(i => ({
          model_name: i.model_name,
          quantity: i.quantity,
          mrp: i.mrp || 0,
          unit_price: i.unit_price || (i.mrp || 0) / 2,
        }))
      };
      const r = await createManualPO(payload);
      toast.success(r.data.message);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create manual PO");
    }
    setCreating(false);
  };

  const totalQty = selectedItems.reduce((sum, i) => sum + i.quantity, 0);
  const totalValue = selectedItems.reduce((sum, i) => sum + (i.unit_price || (i.mrp || 0) / 2) * i.quantity, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="manual-po-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Create Manual PO</h2>
            <p className="text-xs text-slate-500 mt-0.5">Select store and items from inventory</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-manual-po">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><div className="spinner" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Store Selection */}
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-2 block">
                <Store className="w-4 h-4 inline mr-1.5" />Store
              </label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search stores..." value={storeSearch}
                  onChange={e => setStoreSearch(e.target.value)} className="pl-9" data-testid="manual-po-store-search" />
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {filteredStores.map(s => (
                  <button key={s.store_name}
                    onClick={() => setSelectedStore(s.store_name)}
                    data-testid={`select-store-${s.store_name}`}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                      selectedStore === s.store_name
                        ? "bg-slate-800 text-white border-slate-800"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                    }`}>
                    {s.store_name}
                  </button>
                ))}
                {filteredStores.length === 0 && (
                  <p className="text-xs text-slate-400 py-2">No stores found. Add stores in Store Directory first.</p>
                )}
              </div>
            </div>

            {/* Item Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-slate-700">
                  <Package className="w-4 h-4 inline mr-1.5" />Select Items
                </label>
                <Button variant="outline" size="sm" onClick={handleCompleteBatch} data-testid="complete-batch-btn">
                  <Zap className="w-3.5 h-3.5 mr-1" /> Complete Batch (1 each)
                </Button>
              </div>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search inventory..." value={invSearch}
                  onChange={e => setInvSearch(e.target.value)} className="pl-9" data-testid="manual-po-inv-search" />
              </div>
              <div className="border rounded-lg max-h-52 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 text-xs">
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Stock</TableHead>
                      <TableHead className="text-right">MRP</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInventory.slice(0, 100).map(item => {
                      const isSelected = selectedItems.some(i => i.sr_no === item.sr_no);
                      return (
                        <TableRow key={item.sr_no}
                          className={`cursor-pointer text-xs ${isSelected ? "bg-blue-50" : "hover:bg-slate-50"}`}
                          onClick={() => toggleItem({ ...item, maxQty: item.quantity, quantity_available: item.quantity })}
                          data-testid={`inv-item-${item.sr_no}`}>
                          <TableCell>
                            <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                              isSelected ? "bg-blue-600 border-blue-600 text-white" : "border-slate-300"
                            }`}>
                              {isSelected && <CheckCircle className="w-3 h-3" />}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium text-slate-800">{item.model_name}</TableCell>
                          <TableCell className="text-right font-mono">{item.quantity}</TableCell>
                          <TableCell className="text-right font-mono">Rs.{item.mrp?.toLocaleString()}</TableCell>
                          <TableCell className="text-right font-mono">Rs.{(item.unit_price || item.mrp / 2)?.toLocaleString()}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Selected Items with Qty */}
            {selectedItems.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">
                  Selected Items ({selectedItems.length})
                </h3>
                <div className="border rounded-lg max-h-40 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 text-xs">
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right w-24">Qty</TableHead>
                        <TableHead className="text-right">Line Total</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedItems.map(item => (
                        <TableRow key={item.sr_no} className="text-xs">
                          <TableCell className="font-medium">{item.model_name}</TableCell>
                          <TableCell className="text-right">
                            <Input type="number" min={1} max={item.maxQty || 999}
                              value={item.quantity}
                              onClick={e => e.stopPropagation()}
                              onChange={e => updateQty(item.sr_no, parseInt(e.target.value) || 1)}
                              className="h-7 w-20 text-right text-xs ml-auto"
                              data-testid={`qty-${item.sr_no}`} />
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            Rs.{((item.unit_price || (item.mrp || 0) / 2) * item.quantity).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <button onClick={() => setSelectedItems(prev => prev.filter(i => i.sr_no !== item.sr_no))}
                              className="text-red-400 hover:text-red-600">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-slate-50 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {selectedItems.length > 0 && (
              <span>
                <strong>{totalQty}</strong> pcs | <strong>Rs.{totalValue.toLocaleString()}</strong> total
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !selectedStore || selectedItems.length === 0}
              data-testid="create-manual-po-submit">
              {creating ? (
                <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />Creating...</>
              ) : (
                <><Plus className="w-4 h-4 mr-1.5" />Create Manual PO</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
